import { defineStore } from 'pinia';

import type {
  GrammarBadgeProps,
  GrammarBadgeType,
  GrammarTertiaryButtonProps,
  ToastMessage,
  WorkflowToast,
} from '@nora-monorepo/grammar-ui';
import {
  GrammarToastType,
  ToastService,
} from '@nora-monorepo/grammar-ui';

const DEFAULT_DURATION = 3000;

interface BaseToastOptions {
  badgeType?: GrammarBadgeType;
  detail?: string;
  duration?: number;
  message?: string;
}

export type GrammarToastMessageOptions = BaseToastOptions & {
  component: GrammarToastType.ToastMessage;
  leadingBadge?: GrammarBadgeProps;
};

type GrammarTertiaryButtonPropsWithClick = GrammarTertiaryButtonProps & {
  onClick?: (event: Event) => void;
};

export type GrammarWorkflowToastOptions = BaseToastOptions & {
  component: GrammarToastType.WorkflowToast;
  leadingBadge?: GrammarBadgeProps;
  trailingBadge?: GrammarBadgeProps;
  trailingTertiaryButton?: GrammarTertiaryButtonPropsWithClick;
};

/**
 * Pinia store for displaying toasts using the ToastService from grammar-web.
 */
export const useToastStore = defineStore('useToastStore', () => {
  const toastService = new ToastService({ maxVisible: 3, placement: 'bottom-right' });

  /**
   * Creates a grammar tpg-workflow-toast element based on GrammarWorkflowToastOptions
   * https://prod.grammar.teleplan.cloud/components/workflow-toast/
   */
  function createGrammarWorkflowToast(toastOptions: GrammarWorkflowToastOptions): WorkflowToast {
    const toast = document.createElement('tpg-workflow-toast');
    if (toastOptions.leadingBadge) {
      const leadingBadge = document.createElement('tpg-badge');
      leadingBadge.slot = 'leading-badge';
      Object.assign(leadingBadge, toastOptions.leadingBadge);
      leadingBadge.type = toastOptions.badgeType ?? 'neutral';
      toast.appendChild(leadingBadge);
    }

    if (toastOptions.trailingBadge) {
      const trailingBadge = document.createElement('tpg-badge');
      trailingBadge.slot = 'trailing-badge';
      Object.assign(trailingBadge, toastOptions.trailingBadge);
      trailingBadge.type = toastOptions.badgeType ?? 'neutral';
      toast.appendChild(trailingBadge);
    }

    if (toastOptions.trailingTertiaryButton) {
      const tertiaryButton = document.createElement('tpg-tertiary-button');
      tertiaryButton.slot = 'trailing-tertiary-button';
      Object.assign(tertiaryButton, toastOptions.trailingTertiaryButton);

      if (toastOptions.trailingTertiaryButton.onClick) {
        tertiaryButton.addEventListener('tpg-click', toastOptions.trailingTertiaryButton.onClick);
      }
      toast.appendChild(tertiaryButton);
    }
    return toast;
  }

  /**
   * Creates a grammar tpg-toast-message element based on GrammarToastMessageOptions
   * https://prod.grammar.teleplan.cloud/components/toast-message/
   */
  function createGrammarToastMessage(toastOptions: GrammarToastMessageOptions): ToastMessage {
    const toast = document.createElement('tpg-toast-message');
    const leadingBadgeProps: GrammarBadgeProps = toastOptions.leadingBadge ?? {};
    const leadingBadge = document.createElement('tpg-badge');
    leadingBadge.slot = 'leading-badge';

    // We set the emphasis before assigning the rest of the props since it looks weird with a background, but we allow you to change it with props if you want it to look weird on purpose.
    leadingBadge.emphasis = 'no-background-high';

    // Type can also be overridden from the badge props if you want a custom style
    leadingBadge.type = toastOptions.badgeType ?? 'neutral';
    Object.assign(leadingBadge, leadingBadgeProps);

    // If we don't have a leading badge, we fall back to using the older message/detail props.
    if (!toastOptions.leadingBadge) {
      leadingBadge.label = toastOptions.message ?? '';
      leadingBadge.bottomLabel = toastOptions.detail ?? '';
    }

    toast.appendChild(leadingBadge);
    return toast;
  }

  /**
   * Handles creating either a grammar ToastMessage or a WorkflowToast element that is passed to the toast service.
   * https://prod.grammar.teleplan.cloud/web-utilities/toast-service/
   */
  function addToast(toastOptions: GrammarToastMessageOptions | GrammarWorkflowToastOptions): void {
    let toast: ToastMessage | WorkflowToast;

    switch (toastOptions.component) {
      case GrammarToastType.WorkflowToast:
        toast = createGrammarWorkflowToast(toastOptions);
        break;
      case GrammarToastType.ToastMessage:
        toast = createGrammarToastMessage(toastOptions);
        break;
      default:
        throw new Error('Unknown toast type');
    }

    toast.duration = toastOptions.duration ?? DEFAULT_DURATION;
    toastService.add(toast);
  }

  /// Toast Message Helpers
  function addError(options: Omit<GrammarToastMessageOptions, 'component' | 'badgeType'>): void {
    addToast({ ...options, component: GrammarToastType.ToastMessage, badgeType: 'error' });
  }

  function addInfo(options: Omit<GrammarToastMessageOptions, 'component' | 'badgeType'>): void {
    addToast({ ...options, component: GrammarToastType.ToastMessage, badgeType: 'neutral' });
  }

  function addSuccess(options: Omit<GrammarToastMessageOptions, 'component' | 'badgeType'>): void {
    addToast({ ...options, component: GrammarToastType.ToastMessage, badgeType: 'ok' });
  }

  function addWarn(options: Omit<GrammarToastMessageOptions, 'component' | 'badgeType'>): void {
    addToast({ ...options, component: GrammarToastType.ToastMessage, badgeType: 'warning' });
  }

  /// Workflow Toast Helpers
  function addWorkflowToastError(options: Omit<GrammarWorkflowToastOptions, 'component' | 'badgeType'>): void {
    addToast({ ...options, component: GrammarToastType.WorkflowToast, badgeType: 'error' });
  }

  function addWorkflowToastInfo(options: Omit<GrammarWorkflowToastOptions, 'component' | 'badgeType'>): void {
    addToast({ ...options, component: GrammarToastType.WorkflowToast, badgeType: 'neutral' });
  }

  function addWorkflowToastSuccess(options: Omit<GrammarWorkflowToastOptions, 'component' | 'badgeType'>): void {
    addToast({ ...options, component: GrammarToastType.WorkflowToast, badgeType: 'ok' });
  }

  function addWorkflowToastWarn(options: Omit<GrammarWorkflowToastOptions, 'component' | 'badgeType'>): void {
    addToast({ ...options, component: GrammarToastType.WorkflowToast, badgeType: 'warning' });
  }

  return {
    addError,
    addInfo,
    addSuccess,
    addToast,
    addWarn,
    addWorkflowToastError,
    addWorkflowToastInfo,
    addWorkflowToastSuccess,
    addWorkflowToastWarn,
    toastService,
  };
});
